<script lang="ts">
  import { AatmanovaClient } from "@aatmanova/sdk";
  import {
    createQuery,
    createMutation,
    useQueryClient,
  } from "@tanstack/svelte-query";
  import { derived } from "svelte/store";

  import LoginGate from "./components/auth/LoginGate.svelte";
  import Sidebar from "./components/layout/Sidebar.svelte";
  import Header from "./components/layout/Header.svelte";
  import OverviewTab from "./components/overview/OverviewTab.svelte";
  import OrdersTab from "./components/orders/OrdersTab.svelte";
  import QuotesTab from "./components/quotes/QuotesTab.svelte";
  import SettingsTab from "./components/settings/SettingsTab.svelte";
  import ToastStack from "./components/ToastStack.svelte";

  let { client }: { client: AatmanovaClient } = $props();

  const queryClient = useQueryClient();

  // Queries
  const userQuery = createQuery({
    queryKey: ["user"],
    queryFn: () => client.getCurrentUser(),
    retry: false,
  });

  const currentUser = $derived($userQuery.data?.data?.user);

  const ordersQuery = createQuery(
    derived(userQuery, ($userQuery) => ({
      queryKey: ["orders"],
      queryFn: () => client.getOrders(),
      enabled: !!$userQuery.data?.data?.user,
    })),
  );

  const quotesQuery = createQuery(
    derived(userQuery, ($userQuery) => ({
      queryKey: ["quotes"],
      queryFn: async () => {
        const res = await client.getQuotes();
        return res.data || [];
      },
      enabled: !!$userQuery.data?.data?.user,
    })),
  );

  // Toast notifications state
  interface ToastItem {
    id: string;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }
  let toasts = $state<ToastItem[]>([]);

  function addToast(message: string, type: ToastItem["type"] = "success") {
    const id = Math.random().toString(36).substring(2, 9);
    toasts = [...toasts, { id, message, type }];
    setTimeout(() => {
      toasts = toasts.filter((t) => t.id !== id);
    }, 3000);
  }

  // Mutations
  const loginMutation = createMutation({
    mutationFn: (credentials: any) => client.login(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      addToast("Welcome back to Aatmanova!", "success");
    },
    onError: (err: any) => {
      addToast(err.message || "Authentication failed.", "error");
    },
  });

  const registerMutation = createMutation({
    mutationFn: (details: any) => client.register(details),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      addToast(
        "Account created successfully. Welcome to Aatmanova!",
        "success",
      );
    },
    onError: (err: any) => {
      addToast(err.message || "Registration failed.", "error");
    },
  });

  const logoutMutation = createMutation({
    mutationFn: () => client.logout(),
    onSuccess: () => {
      queryClient.setQueryData(["user"], null);
      queryClient.invalidateQueries({ queryKey: ["user"] });
      addToast("Logged out of session.", "info");
    },
  });

  // Authentication Fields
  let email = $state("");
  let password = $state("");
  let name = $state("");
  let isRegistering = $state(false);
  let loginError = $state("");

  // Tab State initialized from URL pathname
  let activeTab = $state(
    typeof window !== "undefined" &&
      ["overview", "orders", "quotes", "settings"].includes(
        window.location.pathname.replace(/^\/|\/$/g, ""),
      )
      ? window.location.pathname.replace(/^\/|\/$/g, "")
      : "overview",
  );

  // Sync tab state changes to URL pathname
  $effect(() => {
    if (typeof window !== "undefined") {
      const currentTab =
        window.location.pathname.replace(/^\/|\/$/g, "") || "overview";
      if (currentTab !== activeTab) {
        window.history.pushState({}, "", "/" + activeTab);
      }
    }
  });

  // Listen to popstate (browser back/forward button clicks)
  $effect(() => {
    if (typeof window !== "undefined") {
      const handlePopState = () => {
        const tab =
          window.location.pathname.replace(/^\/|\/$/g, "") || "overview";
        if (["overview", "orders", "quotes", "settings"].includes(tab)) {
          activeTab = tab;
        }
      };
      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    }
  });

  // Dynamic UI theme state
  let theme = $state(localStorage.getItem("aatmanova_theme") || "dark");

  $effect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    } else {
      document.documentElement.classList.add("light");
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("aatmanova_theme", theme);
  });

  function toggleTheme() {
    theme = theme === "dark" ? "light" : "dark";
  }

  function handleAuthSubmit(e: SubmitEvent) {
    e.preventDefault();
    loginError = "";

    if (isRegistering) {
      $registerMutation.mutate(
        { name, email, password, role: "PATRON" },
        {
          onError: (err: any) => {
            loginError =
              err.message || "Registration failed. Please check input values.";
          },
        },
      );
    } else {
      $loginMutation.mutate(
        { email, password },
        {
          onError: (err: any) => {
            loginError = err.message || "Invalid email or password";
          },
        },
      );
    }
  }
</script>

<main
  class="min-h-screen bg-ambient-flow text-base-content flex flex-col font-sans"
>
  {#if $userQuery.isLoading}
    <div class="flex-1 flex flex-col items-center justify-center gap-4">
      <div
        class="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"
      ></div>
      <p class="text-base-content/75 font-medium animate-pulse">
        Loading secure user session...
      </p>
    </div>
  {:else if !currentUser}
    <LoginGate
      bind:email
      bind:password
      bind:name
      bind:isRegistering
      bind:loginError
      isPending={isRegistering
        ? $registerMutation.isPending
        : $loginMutation.isPending}
      onsubmit={handleAuthSubmit}
    />
  {:else}
    <!-- Responsive Drawer Wrapper -->
    <div class="drawer lg:drawer-open flex-1 min-h-screen">
      <input id="user-drawer" type="checkbox" class="drawer-toggle" />

      <!-- Main Panel Context -->
      <div class="drawer-content flex-1 flex flex-col min-w-0 overflow-y-auto">
        <Header {theme} {toggleTheme} drawerId="user-drawer" />

        <!-- Main Panel Content View -->
        <div class="flex-1 p-4 md:p-8 space-y-8 max-w-7xl w-full mx-auto">
          {#if activeTab === "overview"}
            <OverviewTab
              {currentUser}
              orders={$ordersQuery.data?.data || []}
              quotes={$quotesQuery.data || []}
              setActiveTab={(tab) => (activeTab = tab)}
            />
          {:else if activeTab === "orders"}
            <OrdersTab
              orders={$ordersQuery.data?.data || []}
              ordersQueryIsLoading={$ordersQuery.isLoading}
            />
          {:else if activeTab === "quotes"}
            <QuotesTab
              {client}
              quotes={$quotesQuery.data || []}
              quotesQueryIsLoading={$quotesQuery.isLoading}
              {addToast}
              invalidateQuotes={() =>
                queryClient.invalidateQueries({ queryKey: ["quotes"] })}
              invalidateOrders={() =>
                queryClient.invalidateQueries({ queryKey: ["orders"] })}
              setActiveTab={(tab) => (activeTab = tab)}
            />
          {:else if activeTab === "settings"}
            <SettingsTab {currentUser} {addToast} />
          {/if}
        </div>
      </div>

      <!-- Drawer Side -->
      <div class="drawer-side z-40 lg:overflow-visible">
        <label
          for="user-drawer"
          aria-label="close sidebar"
          class="drawer-overlay"
        ></label>
        <Sidebar
          bind:activeTab
          {currentUser}
          ordersCount={$ordersQuery.data?.data?.length || 0}
          quotesCount={$quotesQuery.data?.length || 0}
          onlogout={() => $logoutMutation.mutate()}
          drawerId="user-drawer"
          theme={theme}
        />
      </div>
    </div>
  {/if}

  <!-- Toast Notification Stack -->
  <ToastStack {toasts} />
</main>
